<template>
  <div v-if="hasBonuses" class="mb-6 overflow-x-auto rounded-lg border border-gray-200">
    <table class="w-full text-sm">
      <caption v-if="title" class="bg-gray-50 px-4 py-3 text-left text-base font-semibold text-gray-900">
        {{ title }}
      </caption>
      <thead>
        <tr class="border-t border-gray-100 text-xs text-gray-500">
          <th scope="col" class="px-2 py-2 text-left font-medium sm:px-4">{{ t("device_price") }}</th>
          <th scope="col" class="px-2 py-2 text-right font-medium sm:px-4">{{ t("customer_price") }}</th>
          <th scope="col" class="hidden bg-gray-50 px-4 py-2 text-right font-medium sm:table-cell">
            {{ t("current_bonus") }}
          </th>
          <th scope="col" class="bg-primary/10 px-2 py-2 text-right font-semibold text-primary-dark sm:px-4">
            {{ t("new_bonus") }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, index) in rowsWithBonus"
          :key="index"
          data-test="bonus-row"
          :class="row.separated ? 'border-t-2 border-gray-200' : 'border-t border-gray-100'"
        >
          <th scope="row" class="px-2 py-2.5 text-left font-medium text-gray-700 sm:px-4">{{ row.label }}</th>
          <td class="px-2 py-2.5 text-right whitespace-nowrap sm:px-4">
            <span class="font-semibold tabular-nums text-gray-900">{{ row.value }}</span>
            <span v-if="unit" class="block text-xs text-gray-500 sm:ml-1 sm:inline">{{ unit }}</span>
          </td>
          <td
            data-test="bonus-current"
            class="hidden bg-gray-50 px-4 py-2.5 text-right whitespace-nowrap tabular-nums text-gray-400 sm:table-cell"
          >
            <template v-if="row.bonus">{{ formatBonus(row.bonus.before) }}</template>
          </td>
          <td class="bg-primary/10 px-2 py-2.5 text-right whitespace-nowrap tabular-nums sm:px-4">
            <span
              v-if="row.bonus"
              class="flex flex-col items-end sm:flex-row sm:items-baseline sm:justify-end sm:gap-2"
            >
              <span data-test="bonus-increase" class="text-xs font-semibold text-green-700">
                +{{ formatBonus(row.bonus.increase) }}
              </span>
              <span data-test="bonus-new" class="text-lg font-bold text-primary-dark">
                {{ formatBonus(row.bonus.after) }}
              </span>
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <PriceTable v-else :title="title" :unit="unit" :rows="rows" />
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { useI18n } from "vue-i18n";
import { formatPrice } from "@estaly/ui";
import PriceTable from "./PriceTable.vue";

// TEMPORARY (EST-951): projects the seller bonus of the new Easy Cash offer until the v3 retribution
// configurations exist (EST-922). Remove this block and its `seller_retributions_price_table` type once the
// announcement is over.
//
// A price grid whose rows also carry the store commission before and after the offer change. For a user
// with a single store, the bonus keeps the same share of the commission, based on the store's current seller
// bonus (monthly offer, same category and price band): a percentage applies to `storeCommissionAfter`, a fixed
// amount is scaled by `storeCommissionAfter / storeCommissionBefore`.
// Without a bonus to show (several stores, no bonus, loading or request failed), it renders the plain price
// grid. Bonuses only show once this mount has loaded them: the persisted store may still hold another user's.
export default {
  name: "SellerRetributionsPriceTable",

  components: { PriceTable },

  props: {
    title: { type: String, default: "" },
    unit: { type: String, default: "" },
    rows: { type: Array, default: () => [] },
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      bonusesLoaded: false,
    };
  },

  computed: {
    ...mapGetters("auth", ["accessibleStoreIds"]),
    ...mapState("retributionConfigurations", ["retributionConfigurations"]),

    hasSingleStore() {
      return this.accessibleStoreIds.length === 1;
    },

    rowsWithBonus() {
      return this.rows.map((row) => ({ ...row, bonus: this.bonusFor(row) }));
    },

    hasBonuses() {
      return this.bonusesLoaded && this.hasSingleStore && this.rowsWithBonus.some((row) => row.bonus);
    },
  },

  async mounted() {
    if (!this.hasSingleStore) return;

    const response = await this.fetchRetributionConfigurations({ perPage: 1000 });
    this.bonusesLoaded = response?.success === true;
  },

  methods: {
    ...mapActions("retributionConfigurations", ["fetchRetributionConfigurations"]),

    formatBonus(amount) {
      return formatPrice(amount);
    },

    bonusFor({ category, priceBandMin, storeCommissionBefore, storeCommissionAfter }) {
      if (!(storeCommissionBefore > 0)) return null;

      const configuration = this.retributionConfigurations.find(
        (config) =>
          config.monthlyBilling && config.category === category && Number(config.priceBandMin) === Number(priceBandMin),
      );
      const before = Number(configuration?.sellerNetAmount);
      if (!(before > 0)) return null;

      const after = configuration.sellerPercentage
        ? Number(configuration.sellerValue) * storeCommissionAfter
        : (before * storeCommissionAfter) / storeCommissionBefore;
      const roundedAfter = Math.round(after * 100) / 100;
      return { before, after: roundedAfter, increase: Math.round((roundedAfter - before) * 100) / 100 };
    },
  },
};
</script>

<i18n src="./SellerRetributionsPriceTable.i18n.json"></i18n>
